/**
 * Delivery rules for checkout and the location picker — pure functions, no Angular, so they are tested
 * directly and the mobile app's copy (lib/models/public/checkout.dart) can be checked against the same
 * cases.
 *
 * Cross-zone delivery (a store delivering from its own zone into another one over a configured route) is
 * the reason this file exists. Every field it adds to the quote is OPTIONAL: an older API sends none of
 * them, and a same-zone quote from the new API sends them in a shape that changes nothing. So every rule
 * here reads the new fields through `text` / `num` and falls back to today's behaviour when they are
 * missing, null or of the wrong type.
 */
import type { DeliveryQuote, DeliveryRewardPolicy } from './checkout.service';
import type { CoinWallet } from './coins.service';
import type { StoreDeliveryZone } from './store.models';
import { zoneContains } from './geo';

type Quote = DeliveryQuote | null | undefined;

// ── Reading the quote ────────────────────────────────────────────────────────

/** A non-blank string, trimmed; anything else (missing, null, blank, another type) is null. */
export function text(v: unknown): string | null {
  return typeof v === 'string' ? v.trim() || null : null;
}

/** A finite number; anything else is null. */
export function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** The quote's reward policy, or null when it is missing or not an object. */
function policyOf(q: Quote): DeliveryRewardPolicy | null {
  const p: unknown = q?.rewardPolicy;
  return p !== null && typeof p === 'object' && !Array.isArray(p) ? (p as DeliveryRewardPolicy) : null;
}

/** A reward-policy flag: the server's default (allowed) unless it says otherwise. */
function allowed(v: unknown): boolean {
  return typeof v === 'boolean' ? v : true;
}

// ── Cross-zone route ─────────────────────────────────────────────────────────

/** A deliverable quote over a route into another zone. Everything route-specific hangs off this. */
export function isCrossZoneQuote(q: Quote): boolean {
  return q?.deliverable === true && q.isCrossZone === true;
}

/**
 * The two zone names for the route line, or null. A same-zone quote from the new API names its zone as
 * both origin and destination; those names are never shown, so the page reads exactly as before.
 */
export function routeNames(q: Quote): { origin: string; destination: string } | null {
  if (!isCrossZoneQuote(q)) return null;
  const origin = text(q?.originZoneName);
  const destination = text(q?.destinationZoneName);
  return origin && destination ? { origin, destination } : null;
}

/**
 * Cross-zone ETA in minutes: the store's prep time plus the route's trip time (the quote carries only the
 * trip, pickup → door). Null on a same-zone quote — its cards keep today's windows — and when the route
 * has no trip time.
 */
export function crossZoneEtaMinutes(q: Quote, prepMinutes: number | null | undefined): number | null {
  if (!isCrossZoneQuote(q)) return null;
  const trip = whole(q?.estimatedDeliveryMinutes);
  if (trip == null || trip <= 0) return null;
  const prep = whole(prepMinutes);
  return trip + (prep == null || prep < 0 ? 0 : prep);
}

/** A whole number of minutes (the server sends ints), or null. */
function whole(v: unknown): number | null {
  const n = num(v);
  return n == null ? null : Math.trunc(n);
}

// ── Refusals and riders ──────────────────────────────────────────────────────

/** Reasons the app's own copy already explains (and translates) — kept as they are. */
const APP_EXPLAINED_REASONS = ['StoreNotZoned', 'NoCoordinates', 'OutsideDeliveryArea'];

/**
 * The server's sentence for a refused quote, or null to keep the app's own copy. The original three
 * reasons stay in the app's words (the outside-area sentence is the same either way); the route
 * refusals — no route to that zone, a local-only store, a route minimum — are shown in the server's
 * words, never rewritten.
 */
export function serverRefusalText(q: Quote): string | null {
  if (!q || q.deliverable) return null;
  const code = text(q.notDeliverableReason);
  if (!code || APP_EXPLAINED_REASONS.includes(code)) return null;
  return text(q.notDeliverableMessage);
}

/** The refusal's reason code, e.g. `RouteNotServed`, or null. */
export function refusalCode(q: Quote): string | null {
  return text(q?.notDeliverableReason);
}

/**
 * The server's no-rider sentence for a route nobody can deliver right now, or null. Delivery is blocked
 * exactly as before (deliverable but no riders); only the wording changes, and only when the server
 * sends one — a same-zone quote never does.
 */
export function ridersText(q: Quote): string | null {
  if (!q?.deliverable || q.ridersAvailable !== false) return null;
  return text(q.ridersUnavailableMessage);
}

// ── The delivery bill ────────────────────────────────────────────────────────

/** Round to cents, as the server does before comparing fees. */
export function round2(x: number): number {
  return Math.round(x * 100 + 1e-9) / 100;
}

export interface DeliveryBill {
  /** The chosen tier's quoted fee, before any reward. */
  tierFee: number;
  /** What a reward takes off it. */
  reduction: number;
  /** What the customer is billed for delivery. */
  payable: number;
}

/**
 * The delivery fee the server will bill — a mirror of its DeliveryChargeCalculator. A reward (voucher or
 * coins) waives the CHOSEN tier, Priority included where the platform's free delivery covers it (otherwise
 * `rewardApplied` is false: see {@link effectiveReward}). On a cross-zone quote the reward policy may cap what
 * it takes off ("up to the local delivery fee"), so the customer pays the rest. A same-zone quote has no
 * policy: the reward waives the whole fee, as it always has. Coupons are not modelled here.
 */
export function deliveryBill(q: Quote, priority: boolean, rewardApplied: boolean): DeliveryBill {
  if (!q || !q.deliverable) return { tierFee: 0, reduction: 0, payable: 0 };
  const tierFee = priority ? (q.priorityFee ?? q.fee) : q.fee;
  const policy = isCrossZoneQuote(q) ? policyOf(q) : null;
  const cap = policy == null ? null : num(priority ? policy.priorityWaivesUpTo : policy.waivesUpTo);
  const reduction = !rewardApplied ? 0 : cap == null ? tierFee : Math.min(tierFee, Math.max(0, cap));
  return { tierFee, reduction, payable: round2(tierFee - reduction) };
}

// ── Rewards ──────────────────────────────────────────────────────────────────

export type RewardKind = 'voucher' | 'coins';
export type RewardChoice = 'none' | RewardKind;
/** What the reward row offers: a toggle, a note that rewards don't apply to this delivery option
 *  (`notForOption`) or this route (`blocked`), the progress hint, or nothing. */
export type RewardOffer = RewardKind | 'notForOption' | 'blocked' | 'hint' | 'none';

/**
 * Whether this kind of reward may pay for this delivery — a mirror of the server's refusal at placement.
 * Always true on a same-zone quote.
 */
export function rewardKindAllowed(kind: RewardKind, q: Quote, priority: boolean): boolean {
  const policy = isCrossZoneQuote(q) ? policyOf(q) : null;
  if (policy == null) return true;
  if (kind === 'voucher' && !allowed(policy.vouchersAllowed)) return false;
  if (kind === 'coins' && !allowed(policy.coinsAllowed)) return false;
  const cap = num(priority ? policy.priorityWaivesUpTo : policy.waivesUpTo);
  return !(cap != null && cap <= 0);
}

/**
 * The reward row to show. Same precedence as always — a voucher, else coins, else the progress hint —
 * with the cross-zone policy deciding whether a reward the customer holds may be used here.
 *
 * `freeDeliveryCoversTier` is the platform's answer for the chosen tier (`PlatformSettingsService
 * .freeDeliveryCovers`: which delivery options a free delivery may cover, Standard only by default). A
 * customer who holds a reward is told when the tier can't take it, before any route policy — placement
 * refuses in that order. The default exists only so the policy rules can be exercised on their own; the
 * page always passes it.
 */
export function rewardOffer(
  wallet: CoinWallet | null | undefined,
  q: Quote,
  priority: boolean,
  freeDeliveryCoversTier = true,
): RewardOffer {
  if (!wallet || !wallet.enabled) return 'none';
  const hasVoucher = wallet.freeDeliveryVouchers > 0;
  const canCoins = wallet.coinRedemptionEnabled && wallet.balance >= wallet.freeDeliveryCoinCost;
  if (!freeDeliveryCoversTier && (hasVoucher || canCoins)) return 'notForOption';
  if (hasVoucher && rewardKindAllowed('voucher', q, priority)) return 'voucher';
  if (canCoins && rewardKindAllowed('coins', q, priority)) return 'coins';
  if (hasVoucher || canCoins) return 'blocked';
  if (wallet.coinRedemptionEnabled) return 'hint';
  return 'none';
}

/**
 * The reward actually sent, billed and struck through: the customer's choice only while its toggle is
 * the one on screen and there is a standard fee to waive. A reward toggled earlier is not spent on a
 * delivery that has become free, on a tier the platform's free delivery doesn't cover, nor on one whose
 * policy no longer allows it. `freeDeliveryCoversTier` as in {@link rewardOffer}.
 */
export function effectiveReward(
  chosen: RewardChoice,
  wallet: CoinWallet | null | undefined,
  q: Quote,
  priority: boolean,
  isDelivery: boolean,
  freeDeliveryCoversTier = true,
): RewardChoice {
  return isDelivery && chosen !== 'none' && !!q && q.deliverable && q.fee > 0 &&
    rewardOffer(wallet, q, priority, freeDeliveryCoversTier) === chosen
    ? chosen
    : 'none';
}

// ── Keys and the fee the customer saw ────────────────────────────────────────

/** Identifies the inputs of one quote request — a quote answers only the inputs it was asked about. */
export function deliveryQuoteKey(
  storeId: string,
  lat: number | null | undefined,
  lng: number | null | undefined,
  subtotal: number,
): string {
  return `${storeId}|${lat ?? ''}|${lng ?? ''}|${subtotal}`;
}

/** Identifies the inputs of one delivery fee: the quote's inputs plus the tier and the reward. */
export function deliveryFeeKey(quoteKey: string, priority: boolean, reward: RewardChoice): string {
  return `${quoteKey}|${priority ? 'priority' : 'standard'}|${reward}`;
}

/**
 * The `expectedDeliveryFee` to send with an order, or undefined to leave it out. Sent only when the page
 * knows the fee it showed: a Delivery order without a coupon (the page can't predict a coupon's free
 * delivery), and either the fee a 409 just reported for these same inputs or a deliverable quote for the
 * current inputs. If the server would bill anything else it refuses with 409 before writing a thing.
 */
export function expectedDeliveryFeeFor(a: {
  delivery: boolean;
  hasCoupon: boolean;
  override: { key: string; fee: number } | null;
  feeKey: string;
  quoteInHand: boolean;
  quote: DeliveryQuote | null;
  priority: boolean;
  rewardApplied: boolean;
}): number | undefined {
  if (!a.delivery || a.hasCoupon) return undefined;
  if (a.override && a.override.key === a.feeKey) return a.override.fee;
  if (!a.quoteInHand || !a.quote || !a.quote.deliverable) return undefined;
  return deliveryBill(a.quote, a.priority, a.rewardApplied).payable;
}

// ── Location picker ──────────────────────────────────────────────────────────

/** A zone the store reaches over a route rather than one of its own. Entries without `kind` are its own. */
export function isCrossZoneEntry(z: StoreDeliveryZone): boolean {
  return z.kind === 'CrossZone';
}

export type ZoneVerdict =
  | { kind: 'none' }
  | { kind: 'home' }
  | { kind: 'cross'; originZoneName: string | null; fee: number }
  | { kind: 'outside' };

/**
 * Where a pin falls among the store's delivery zones: one of its own zones wins over a route, the first
 * route (in the server's order) wins among routes, and a pin in none of them is outside — the same
 * "outside" as before the routes existed.
 */
export function zoneVerdict(zones: StoreDeliveryZone[], lat: number, lng: number): ZoneVerdict {
  if (zones.length === 0) return { kind: 'none' };
  if (zones.some((z) => !isCrossZoneEntry(z) && zoneContains(z, lat, lng))) return { kind: 'home' };
  const route = zones.find((z) => isCrossZoneEntry(z) && zoneContains(z, lat, lng));
  if (route) return { kind: 'cross', originZoneName: text(route.originZoneName), fee: num(route.deliveryFee) ?? 0 };
  return { kind: 'outside' };
}
